import { useCallback, useMemo } from 'react'

import { TronWeb } from 'tronweb'

import { useQuery } from '@tanstack/react-query'

import { apiGetUserBalanceForToken } from '@src/api/machprotocol.api'
import { ChainId, NATIVE_TOKEN_ADDRESS } from '@src/configs/chainInfo'
import abi from '@src/contract/abi/tronOrderBookV2.abi.json'
import { Address } from '@src/types/token'
import { Balance } from '@src/utils/balance'
import { tronLogger } from '@src/utils/logger'
import { useTokensStore } from '@src/zustand_stores'

export const isValidTronAddress = (address: string) => {
    return address === NATIVE_TOKEN_ADDRESS[ChainId.Tron] || TronWeb.isAddress(address)
}

export const waitTron = () => {
    return new Promise((resolve, reject) => {
        let attempts = 0
        const maxAttempts = 1000
        const checkTron = () => {
            if (window.tronWeb) {
                resolve(true)
                return
            }
            attempts++
            if (attempts >= maxAttempts) {
                reject(false)
                return
            }
            setTimeout(checkTron, 100)
        }
        checkTron()
    })
}

export const getTokenBalance = async (tronWeb: any, contractAddress: string, walletAddress: string) => {
    try {
        const contract = await tronWeb.contract().at(contractAddress)
        const decimals = await contract.decimals().call()
        const balance = await contract.balanceOf(walletAddress).call()
        const symbol = await contract.symbol().call()

        const formattedBalance = tronWeb.fromSun(balance.toString())
        return {
            symbol,
            balance: formattedBalance,
            decimals: decimals,
        }
    } catch (err) {
        tronLogger.error(`Error getting balance for ${contractAddress}:`, {
            error: err,
        })
    }
}

export const useTronTokenBalance = (walletAddress: string, tokenAddress: string) => {
    const { tokens: zustandTokens } = useTokensStore()

    // Find token info from available tokens
    const tokenInfo = useMemo(() => {
        if (tokenAddress === NATIVE_TOKEN_ADDRESS[ChainId.Tron]) {
            return {
                decimals: 6,
                symbol: 'TRX',
                name: 'Tronix',
            }
        }
        return zustandTokens.find(
            t => t.chainId === ChainId.Tron && t.address.toLowerCase() === tokenAddress.toLowerCase()
        )
    }, [tokenAddress, zustandTokens])

    const balanceQuery = useQuery({
        queryKey: ['tronTokenBalance', walletAddress, tokenAddress],
        queryFn: async () => {
            if (!walletAddress || !isValidTronAddress(tokenAddress)) {
                throw new Error('Invalid addresses provided')
            }

            try {
                const result = await apiGetUserBalanceForToken(
                    walletAddress,
                    'tron',
                    tokenAddress === NATIVE_TOKEN_ADDRESS[ChainId.Tron] ? 'native' : tokenAddress
                )

                if (tokenAddress === NATIVE_TOKEN_ADDRESS[ChainId.Tron]) {
                    tronLogger.debug('Result', { result })
                }

                if (!tokenInfo) {
                    throw new Error('Token information not found in available tokens list')
                }

                return new Balance({
                    symbol: tokenInfo.symbol,
                    name: tokenInfo.name,
                    decimals: tokenInfo.decimals,
                    bigintValue: BigInt(result.balance),
                    chainId: ChainId.Tron,
                    address: tokenAddress as Address,
                })
            } catch (error) {
                throw new Error(error instanceof Error ? error.message : 'Failed to fetch token balance')
            }
        },
        enabled: !!walletAddress && !!tokenAddress && isValidTronAddress(tokenAddress) && !!tokenInfo,
        refetchOnWindowFocus: false,
    })

    return {
        balance: balanceQuery.data,
        isLoading: balanceQuery.isLoading,
        error: balanceQuery.error ? balanceQuery.error.message : null,
        refetch: balanceQuery.refetch,
    }
}

export const getTronTransactionReceipt = async (hash: string) => {
    try {
        const tronExists = await waitTron()

        if (!tronExists || !window.tronWeb) {
            tronLogger.warn('Please login into Tronlink wallet extension!')
            return false
        }

        // First try to get unconfirmed transaction info
        const unconfirmedInfo = await window.tronWeb.trx.getUnconfirmedTransactionInfo(hash)
        if (unconfirmedInfo && unconfirmedInfo.receipt?.result === 'SUCCESS') {
            return unconfirmedInfo
        }

        // If not found in unconfirmed, check regular transaction info
        const transactionInfo = await window.tronWeb.trx.getTransactionInfo(hash)
        if (transactionInfo && transactionInfo.receipt?.result === 'SUCCESS') {
            return transactionInfo
        }

        return false
    } catch (error) {
        tronLogger.error('Error getting transaction receipt:', { error })
        return false
    }
}

export const useTronTokenApproval = () => {
    const waitForApproval = useCallback(async (hash: string) => {
        try {
            const tronExists = await waitTron()

            if (!tronExists) {
                tronLogger.warn('Please login into Tronlink wallet extension!')
                return false
            }

            if (typeof window.tronWeb === 'undefined') {
                tronLogger.warn('TronWeb is not initialized.')
                return false
            }

            // Fetch transaction info
            const transactionReceipt = await window.tronWeb.trx.getTransactionInfo(hash)

            if (transactionReceipt) {
                tronLogger.debug('Transaction Receipt:', { transactionReceipt })
                return true
            } else {
                tronLogger.debug('Transaction not found or still pending.')
                return false
            }
        } catch (error) {
            tronLogger.error('Error waiting for Tron approval:', { error })
            return false
        }
    }, [])

    const approveToken = useCallback(
        async (contractAddress: string, ownerAddress: string, spenderAddress: string, amount: bigint) => {
            try {
                const tronExists = await waitTron()
                if (
                    !tronExists ||
                    !contractAddress ||
                    !ownerAddress ||
                    !spenderAddress ||
                    amount <= 0 ||
                    !window.tronWeb
                ) {
                    throw new Error('Non-enough paramaters')
                }

                const contract = await window.tronWeb.contract().at(contractAddress)

                const hash = await contract.approve(spenderAddress, amount).send({
                    feeLimit: 1000000000,
                })

                return hash
            } catch (error) {
                tronLogger.error('Error approving token:', { error })
            }
        },
        []
    )

    return { approveToken, waitForApproval }
}

export const writeContractFunction = async (contractAddress: string, functionName: string, params: any[]) => {
    try {
        const tronExists = await waitTron()
        if (!tronExists || !contractAddress || !functionName || !window.tronWeb) {
            throw new Error('Non-enough paramaters')
        }

        const contract = window.tronWeb.contract(abi, contractAddress)

        tronLogger.debug('Parameters', { params })

        const txHash = await contract[functionName](...params).send({
            feeLimit: 1000000000,
        })

        tronLogger.debug(`Function "${functionName}" executed successfully.`, {
            hash: txHash,
        })
        return txHash
    } catch (error) {
        tronLogger.error('Error writing contract function:', { error })
    }
}

export const sleep = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms))
}

export const publicTronWeb = new TronWeb({
    fullHost: 'https://api.trongrid.io', // Use the full node endpoint
})

// Function to get Tron energy price using publicTronWeb
export const getTronEnergyPrice = async () => {
    try {
        // Fetch energy price using publicTronWeb
        const params = await publicTronWeb.trx.getChainParameters()
        if (params) {
            const energyPrice = params.find(o => o.key === 'getEnergyFee')?.value // Price in Sun

            // Fetch bandwidth price (fixed at 1 Sun per unit)
            const bandwidthPrice = 1

            return { energyPrice, bandwidthPrice }
        } else {
            throw new Error('Cannot get Tron energy price.')
        }
    } catch (error) {
        tronLogger.error('Error fetching resource prices:', { error })
        tronLogger.error('PublicTronWeb:', { publicTronWeb })
        throw error
    }
}

/**
 * Updated to compute required energy even if no tronlink is installed by using public TronWeb client
 * and dummy wallet address.
 * @param contractAddress
 * @param functionSelector
 * @param parameters
 * @param usePublicTronWeb
 * @param useDummyOwner
 * @returns
 */
export const estimateTronEnergyUsage = async (
    contractAddress: string,
    functionSelector: string,
    parameters: { type: string; value: unknown }[],
    usePublicTronWeb: boolean = false,
    ownerWallet: Address | undefined
) => {
    try {
        let tronWebInstance: TronWeb
        if (!usePublicTronWeb) {
            const tronExists = await waitTron()
            if (!tronExists || !contractAddress || !functionSelector || !window.tronWeb) {
                throw new Error('Non-enough paramaters')
            }

            // Fallback to this if no tronWeb is specified.
            tronWebInstance = window.tronWeb
        } else {
            tronWebInstance = publicTronWeb
        }

        const result = await tronWebInstance.transactionBuilder.triggerConstantContract(
            contractAddress,
            functionSelector,
            {},
            parameters,
            ownerWallet ? TronWeb.address.toHex(ownerWallet) : undefined
        )

        tronLogger.debug('[estimateTronEnergyUsage]: tron gas estimation result', {
            result,
        })

        if (result && result.energy_used !== undefined) {
            return result.energy_used
        } else {
            throw new Error('Failed to estimate energy.')
        }
    } catch (error: any) {
        tronLogger.error(`Error estimating gas: ${error.message}`, {
            contractAddress,
            functionSelector,
            parameters,
        })
        throw error
    }
}

export const getTronAllowance = async (contractAddress: string, ownerAddress: string, spenderAddress: string) => {
    try {
        const tronExists = await waitTron()
        if (!tronExists || !contractAddress || !ownerAddress || !spenderAddress || !window.tronWeb) {
            throw new Error('Non-enough paramaters')
        }

        const contract = await window.tronWeb.contract().at(contractAddress)
        const currentAllowedAmount = await contract.allowance(ownerAddress, spenderAddress).call()
        tronLogger.debug('Current allowed amount:', {
            amount: currentAllowedAmount.toString(),
        })
        return currentAllowedAmount
    } catch (error: any) {
        tronLogger.error('Error getting allowance:', { error })
        throw new Error(`Error getting allowance: ${error.message}`)
    }
}
